import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Link from '@mui/material/Link';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpIcon from '@mui/icons-material/Help';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { IconButton } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import ConfigurableList from './TextEditor';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import { useAuthFetch } from "../api/authFetch";
import { useState, useEffect, useCallback } from 'react';

interface textEditorConfigItem {
  systemName: string;
  name: string;
  value: any;
}

const INITIAL_TEXT_EDITOR_CONFIG: textEditorConfigItem[] = [
  { systemName: 'common.back', name: 'Tlačítko zpět', value:'' },
  { systemName: 'common.confirmChanges', name: 'Tlačítko potvrdit změny', value:'' },
  { systemName: 'common.cancelChanges', name: 'Tlačítko zrušit změny', value:'' },
  { systemName: 'common.display', name: 'Tlačítko zobrazit', value:'' },
  { systemName: 'common.settings', name: 'Tlačítko nastavení', value:'' },
  { systemName: 'common.confirmSettings', name: 'Tlačítko potvrdit nastavení', value:'' },
  { systemName: 'home.content', name: 'Obsah CB nahoře', value:'' },
  { systemName: 'home.actions.allow', name: 'Tlačítko souhlasím', value:'' },
  { systemName: 'home.actions.reject', name: 'Tlačítko nesouhlasím', value:'' },
  { systemName: 'home.actions.settings', name: 'Tlačítko upravit nastavení', value:'' },
  { systemName: 'home.actions.settingsOr', name: 'Nebo', value:'' },
  { systemName: 'footer.content', name: 'Obsah CB dole', value:'' },
  { systemName: 'footer.purposesMore', name: 'Obsah CB dole shrnutí účely', value:'' },
  { systemName: 'footer.purposesExact', name: 'Obsah CB dole účely shrnutí', value:'' },
  { systemName: 'footer.purposesCount', name: 'Obsah CB dole účely počet', value:'' },
  { systemName: 'categoriesSettings.title', name: 'Nastavení - tituel', value:'' },
  { systemName: 'categoriesSettings.content', name: 'Nastavení - obsah', value:'' },
  { systemName: 'categoriesSettings.required.subtitle', name: 'Nastavení - povinné - podtitulek', value:'' },
  { systemName: 'categoriesSettings.required.count', name: 'Nastavení - povinné - počet', value:'' },
  { systemName: 'categoriesSettings.required.name', name: 'Nastavení - povinné - jméno', value:'' },
  { systemName: 'categoriesSettings.required.description', name: 'Nastavení - povinné - popis', value:'' },
  { systemName: 'categoriesSettings.other.subtitle', name: 'Nastavení - další - podtitulek', value:'' },
  { systemName: 'categoriesSettings.other.countDesktop', name: 'Nastavení - další - počet na desktopu', value:'' },
  { systemName: 'categoriesSettings.other.countMobile', name: 'Nastavení - další - počet na mobile', value:'' },
  { systemName: 'categorySettings.content', name: 'Nastavení kategorie - obsah', value:'' },
  { systemName: 'categorySettings.allowed', name: 'Nastavení kategorie - povolené', value:'' },
  { systemName: 'categoryRequired.title', name: 'Katerorie - tituelk', value:'' },
  { systemName: 'categoryRequired.label', name: 'Kategorie - text', value:'' },
  { systemName: 'categoryRequired.cookiesCount', name: 'Kategorie - počet cookies', value:'' },
  { systemName: 'categoryRequired.content', name: 'Kategorie - obsah', value:'' },
  { systemName: 'waulterLogin.title', name: 'Login - titulek', value:'' },
  { systemName: 'waulterLogin.description', name: 'Login - popis', value:'' },
  { systemName: 'home.linkPrivacy', name: 'Link privacy - text', value:'' },
  { systemName: 'home.linkCookies', name: 'Ling cookies - text', value:'' },
  { systemName: 'home.credit', name: 'Waulter prověřil - text', value:'' },
];

/**
 * Independent Agreement Dialog Component
 *
 * This component provides a comprehensive dialog for editing agreement details
 * with multiple tabs for different aspects of the agreement. It handles its own
 * data loading and saving independently.
 *
 * @param open - Controls dialog visibility
 * @param onClose - Callback when dialog is closed
 * @param id - The ID of the agreement to load/edit (use 'new' for creating new)
 * @param agreementId - Alternative to id, the agreement ID to load/edit
 * @param onSuccess - Callback when save is successful
 *
 * Note: Either id or agreementId must be provided. If both are provided, id takes precedence.
 */
interface AgreementDialogProps {
  open: boolean;
  onClose: () => void;
  agreementId?: string;
  id?: string;
  onSuccess?: () => void;
}

export default function AgreementDialog({
  open,
  onClose,
  agreementId,
  id,
  onSuccess
}: AgreementDialogProps) {
  // Determine which ID to use (id takes precedence if both are provided)
  const effectiveId = id || agreementId;
  const authFetch = useAuthFetch();
  
  // Internal state
  const [item, setItem] = useState<any>({});
  const [textEditorConfig, setTextEditorConfig] = useState<textEditorConfigItem[]>(INITIAL_TEXT_EDITOR_CONFIG);
  const [codebookAgreementTypes, setCodebookAgreementTypes] = useState<any[]>([]);
  const [codebookPurposes, setCodebookPurposes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = React.useState('1');
  
  // Add purpose dialog state
  const [openSubDialog, setOpenSubDialog] = useState(false);
  const [addPurposeItem, setAddPurposeItem] = useState('');

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  // Helper functions
  function createTextJsonString(inputArray: textEditorConfigItem[]): string {
    const resultObject: { [key: string]: any } = {};
    inputArray.forEach(item => {
      resultObject[item.systemName] = item.value;
    });
    return JSON.stringify(resultObject, null, 2);
  }

  function parseTextJson(jsonString: string, collection: textEditorConfigItem[]): textEditorConfigItem[] {
    const updatedCollection = collection.map(item => ({ ...item }));
    if (jsonString === undefined) return updatedCollection;
    const jsonObject = JSON.parse(jsonString);
    updatedCollection.forEach(item => {
      if (jsonObject.hasOwnProperty(item.systemName)) {
        item.value = jsonObject[item.systemName];
      }
    });
    return updatedCollection;
  }

  // API functions
  const loadAgreement = useCallback(async () => {
    if (effectiveId === 'new') {
      setItem({id:'new', actualVersion:"0.0", purposes:[], type:''});
      return;
    }
    
    setLoading(true);
    try {
      
      // Determine which parameter to use in the API call
      const paramName = id ? 'id' : 'agreementId';
      const paramValue = id || agreementId;
      
      const response = await authFetch(`${process.env.REACT_APP_AGREEMENT_URL}?${paramName}=${encodeURIComponent(String(paramValue))}`);
      
      if (response.ok) {
        const agreement = await response.json();
        setItem(agreement);
        setTextEditorConfig(parseTextJson(agreement.template, INITIAL_TEXT_EDITOR_CONFIG));
      }
    } catch (error) {
      console.error('Error loading agreement:', error);
    } finally {
      setLoading(false);
    }
  }, [effectiveId, id, agreementId, authFetch]);

  const loadCodebooks = useCallback(async () => {
    try {
      
      // Load agreement types
      const response = await authFetch(process.env.REACT_APP_CODEBOOK_URL +"?codebookId=C003");
      if (response.ok) {
        const codebook = await response.json();
        setCodebookAgreementTypes(codebook.items);
      }
      
      // Load purposes
      const purposesResponse = await authFetch(process.env.REACT_APP_CODEBOOK_URL +"?codebookId=C002");
      if (purposesResponse.ok) {
        const purposesCodebook = await purposesResponse.json();
        setCodebookPurposes(purposesCodebook.items);
      }
    } catch (error) {
      console.error('Error loading codebooks:', error);
    }
  }, [authFetch]);

  const handleSave = async (operationEnum: string) => {
    setLoading(true);
    try {
      const itemToSave = { ...item };
      itemToSave.template = createTextJsonString(textEditorConfig);
      itemToSave.operationEnum = operationEnum;

      const requestOptions = {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json' },
        body: JSON.stringify(itemToSave)
      };
      
      const response = await authFetch(process.env.REACT_APP_AGREEMENT_URL, requestOptions);
      if (response.ok) {
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Error saving agreement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    setItem((prevState:any) => ({
      ...prevState,
      type: event.target.value,
    }));
  };



  const handleDeleteClick = (id: number) => {
    const updatedPurposes = item.purposes.filter((purpose:any) => purpose.id !== id);
    setItem((prevState:any) => ({
      ...prevState,
      purposes: updatedPurposes
    }));
  };

  // Add purpose functions
  const addPurpose = async() => {
    setAddPurposeItem('');
    setOpenSubDialog(true);
  };

  const handlePurpleClose = () => {
    setOpenSubDialog(false);
  };

  const handleAddPurpose = () => {
    // Check if added item is not already added
    if (item.purposes.filter((item:any) => item.code === addPurposeItem).length > 0) {
      return; // Already added
    }

    const selectedPurposeCodebook:any = codebookPurposes.find((i:any) => i.code === addPurposeItem);
    if (selectedPurposeCodebook) {
      const newPurposes = [...item.purposes, {
        id: addPurposeItem, 
        code: addPurposeItem, 
        name: selectedPurposeCodebook.name, 
        description: selectedPurposeCodebook.description,
        category: selectedPurposeCodebook.category
      }];
      setItem((prevState:any) => ({
        ...prevState,
        purposes: newPurposes
      }));
    }
    setOpenSubDialog(false);
  };

  // Load data when dialog opens
  useEffect(() => {
    if (open && effectiveId) {
      loadAgreement();
      loadCodebooks();
    }
  }, [open, effectiveId, loadAgreement, loadCodebooks]);

  const columnsPurposes: GridColDef[] = [
    { field: 'code', headerName: 'Code', width: 65},
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'description', headerName: 'Description', width: 190 },
    { field: 'category', headerName:'Category', width: 200},
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleDeleteClick(params.row.id)}>
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      PaperProps={{
        sx: {
          width: 1000,
          maxHeight: 1000
        }
      }}
    >
      <DialogTitle>Agreement {item.id || effectiveId}</DialogTitle>
      {loading && (
        <Box sx={{ width: '100%', p: 2 }}>
          <LinearProgress />
        </Box>
      )}
      <DialogContent dividers style={{height:'1000px'}}>
        <TabContext value={tabValue}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={handleChange} aria-label="lab API tabs example">
              <Tab label="Agreement" value="1" />
              <Tab label="Abstract" value="2" />
              <Tab label="Content" value="3" />
              <Tab label="Cookie bot - text editor" value="4" />
            </TabList>
          </Box>
          <TabPanel value="1">
            <TextField
              helperText="Name"
              placeholder="Name"
              value={item.name}
              fullWidth 
              onChange={(event:any) => setItem((prevState:any) => ({
                ...prevState,
                name: event.target.value,
              }))}
            />
            <Grid container spacing={2} columns={16}>
              <Grid item xs={6}>
                <TextField
                  helperText="Actual version"
                  placeholder="Actual version"
                  value={item.actualVersion}
                  fullWidth 
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  helperText="Forced version"
                  placeholder="Forced version"
                  value={item.forceVersion}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={4} >
                <FormControlLabel control={<Checkbox checked={item.forceUpdate} 
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setItem((prevState:any) => ({
                    ...prevState,
                    forceUpdate: event.target.checked,
                  }))} />} label="Force update of con." sx={{pl:1}} />
              </Grid>
              <Grid item xs={12}>
                <FormControl sx={{minWidth: 500 }} >
                  <InputLabel id="demo-select-small-label">Type</InputLabel>
                  <Select
                    labelId="demo-select-small-label"
                    id="demo-select-small"
                    value={item.type}
                    fullWidth
                    label="Type"
                    onChange={handleTypeChange}
                  >
                    {codebookAgreementTypes.map((option:any) => (
                      <MenuItem value={option.code} key={option.code}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4} sx={{ alignSelf:'center'}}>
                <FormControlLabel control={<Checkbox checked={item.changePurpose} 
                  onChange={(event:any) => setItem((prevState:any) => ({
                    ...prevState,
                    changePurpose: event.target.checked,
                  }))} />} label="Can change purposes" sx={{pl:1}} />
              </Grid>
              <Grid item xs={8}>
                <Tooltip title={`Kdy zapnout GCM? Pokud jste aktivním uživatelem Google Ads, GA4, AdSense a cílíte na spotřebitele v EU/EHP.
                
                  Při povolení pouze 'Zapnout GCM' se implementuje základní Google consent mode, který spustí pouze povolené značky na základě souhlasu uživatele.`}>
                  <IconButton>
                    <HelpIcon />
                  </IconButton>
                </Tooltip>
                <FormControlLabel control={<Checkbox checked={item.enableGCM2}                     
                  onChange={(event:any) => setItem((prevState:any) => ({
                    ...prevState,
                    enableGCM2: event.target.checked,
                  }))} />}
                  label="Enable Google Consent Mode" 
                  disabled={(item.type !== 'CT004')}
                  sx={{pl:1}} />
              </Grid>
              <Grid item xs={8}>
                <Tooltip title={`Aktivací možnosti "Povolit spuštění Google tagů před souhlasem", zajistíte spuštění Google tags ještě před souhlasem uživatele na vaší cookie liště. Tato volba využívá tzv. cookieless pings a shromažďuje pouze základní údaje pro behaviorální modelování a modelování konverzí, které vyplní mezery v datech.`}>
                  <IconButton>
                    <HelpIcon />
                  </IconButton>
                </Tooltip>
                <FormControlLabel control={<Checkbox checked={item.enableGCM2Advanced && item.type === 'CT004'} 
                  onChange={(event:any) => setItem((prevState:any) => ({
                    ...prevState,
                    enableGCM2Advanced: event.target.checked,
                  }))} />} 
                  label="Allow Google tags to run before consent" 
                  disabled={(item.type !== 'CT004')}
                  sx={{pl:1}} />
              </Grid>
            </Grid>
            <Link href={'https://waulter.eu/agreement?agreementVersionId='+item.permalink} target='_blank' underline="none" sx={{borderSpacing:3}} variant='inherit'>
              {'https://waulter.eu/agreement?ageementVersionId='+item.permalink}
            </Link>
            <TextField
              helperText="Description (public)"
              placeholder="Description (public)"
              value={item.description}
              multiline
              rows={4}
              fullWidth
              onChange={(event:any) => setItem((prevState:any) => ({
                ...prevState,
                description: event.target.value,
              }))}
            />

            <Button variant="contained" onClick={addPurpose}>Add</Button>
            <DataGrid
              rows={item.purposes || []}
              columns={columnsPurposes}
              pageSize={13}
              rowsPerPageOptions={[13]}
              disableSelectionOnClick
              autoHeight={true}
              experimentalFeatures={{ newEditingApi: true }}
            />
          </TabPanel>

          <TabPanel value="2">
            <TextField
              helperText="Agreement abstract (public content HTML)"
              placeholder="Agreement abstract (public content HTML)"
              value={item.abstractText}
              multiline
              rows={25}
              fullWidth
              onChange={(event:any) => setItem((prevState:any) => ({
                ...prevState,
                abstractText: event.target.value,
              }))}
            />
          </TabPanel>

          <TabPanel value="3">
            <TextField
              helperText="Agreement content (public content HTML)"
              placeholder="Agreement content (public content HTML)"
              value={item.content}
              multiline
              rows={25}
              fullWidth
              onChange={(event:any) => setItem((prevState:any) => ({
                ...prevState,
                content: event.target.value,
              }))}
            />
          </TabPanel>
          <TabPanel value="4">
            <span>Template ID: </span>
            <Select name="templateID" label="Template ID" sx={{minWith:300}} value={item.templateID}                       
              onChange={(event:any) => setItem((prevState:any) => ({
                ...prevState,
                templateID: event.target.value,
              }))}>
              <MenuItem key='waulter_default_1' value='waulter_default_1'>Waulter default 1</MenuItem>
              <MenuItem key='waulter_default_2' value='waulter_default_2'>Waulter default 2</MenuItem>
              <MenuItem key='waulter_minimal_1' value='waulter_minimal_1'>Waulter minimal 1</MenuItem>
            </Select> <br/>
            <ConfigurableList config={textEditorConfig} />
          </TabPanel>
        </TabContext>
      </DialogContent>

      {/* Add Purpose Dialog */}
      <Dialog
        open={openSubDialog}
        PaperProps={{
          sx: {
            width: "500px",
            maxWidth: "500px!important",
            height: "300px",
            maxHeight:"300px!important"
          },
        }}
      >
        <DialogTitle>Add Purpose</DialogTitle>
        <DialogContent dividers>
          <FormControl sx={{ m: 1, minWidth: 320 }} size="small">
            <InputLabel id="demo-select-small-label">Purpose</InputLabel>
            <Select
              labelId="demo-select-small-label"
              id="demo-select-small"
              value={addPurposeItem}
              autoWidth
              label="Purpose"
              onChange={(event: SelectChangeEvent) => setAddPurposeItem(event.target.value)}
            >
              {codebookPurposes.map((option:any) => (
                <MenuItem value={option.code} key={option.code}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={handleAddPurpose}>Add</Button>
          <Button onClick={handlePurpleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <DialogActions>
          <Button variant="contained" onClick={() => handleSave('createMinorVersion')} disabled={!(item.last || item.id==='new') }>Create minor version</Button>
          <Button onClick={() => handleSave('createMajorVersion')} disabled={!(item.last || item.id==='new')}>Create major version</Button>
          <Button onClick={() => handleSave('publishActualVersion')} disabled={!item.last || item.actual}>Publish actual version</Button>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
    </Dialog>
  );
} 